import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, ModalBody, ModalFooter, Spinner } from "reactstrap";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBolt,
  faCamera,
  faCircleHalfStroke,
  faCrop,
  faDownload,
  faHand,
  faImage,
  faLayerGroup,
  faLocationDot,
  faMoon,
  faRotateLeft,
  faRotateRight,
  faSun,
  faUndo,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";
import { useIntl } from "react-intl";
import { AiImageCropModal } from "./AiImageCropModal";
import { applyCanvasFilterToBlob } from "../../../cores/helpers/apply-canvas-filter-to-blob";
import {
  applyMagicLayerMove,
  extractMagicLayerPatch,
  type MagicLayerRectPct,
} from "../../../cores/helpers/apply-magic-layer-move";
import { rotateImageSrcToBlobUrl } from "../../../cores/helpers/rotate-image-to-blob-url";
import socialApiClient from "../../../cores/helpers/services/social-api-client";
import "./AiImageViewerEditorModal.scss";

type FilterPreset = {
  id: string;
  labelKey: string;
  css: string;
  icon: IconDefinition;
};

const FILTER_PRESETS: FilterPreset[] = [
  { id: "none", labelKey: "app.aiGeneration.viewerFilterOriginal", css: "", icon: faImage },
  {
    id: "grayscale",
    labelKey: "app.aiGeneration.viewerFilterBw",
    css: "grayscale(1)",
    icon: faCircleHalfStroke,
  },
  { id: "sepia", labelKey: "app.aiGeneration.viewerFilterSepia", css: "sepia(0.55)", icon: faCamera },
  {
    id: "warm",
    labelKey: "app.aiGeneration.viewerFilterWarm",
    css: "sepia(0.12) saturate(1.15) brightness(1.03)",
    icon: faSun,
  },
  {
    id: "cool",
    labelKey: "app.aiGeneration.viewerFilterCool",
    css: "saturate(1.1) hue-rotate(8deg) brightness(1.02)",
    icon: faMoon,
  },
  {
    id: "vivid",
    labelKey: "app.aiGeneration.viewerFilterVivid",
    css: "contrast(1.12) saturate(1.2)",
    icon: faBolt,
  },
];

function buildFilterCss(presetCss: string, brightnessPct: number): string {
  const b = brightnessPct / 100;
  const parts = [presetCss.trim(), `brightness(${b})`].filter(Boolean);
  return parts.join(" ");
}

type ImagePin = {
  key: string;
  xPct: number;
  yPct: number;
};

type MagicLayerState = {
  id: string;
  type: "text" | "logo" | "icon" | "shape" | "graphic";
  label: string;
  text: string | null;
  draftText: string;
  source: MagicLayerRectPct;
  current: MagicLayerRectPct;
  patchUrl: string | null;
};

function clampPct(n: number) {
  return Math.max(0, Math.min(100, Math.round(n * 10) / 10));
}

function rectsEqual(a: MagicLayerRectPct, b: MagicLayerRectPct) {
  return (
    a.xPct === b.xPct &&
    a.yPct === b.yPct &&
    a.wPct === b.wPct &&
    a.hPct === b.hPct
  );
}

function buildPinnedAiPrompt(userPrompt: string, pins: ImagePin[]) {
  const instruction = userPrompt.trim();
  if (!pins.length) return instruction;
  const zones = pins
    .map(
      (pin, index) =>
        `${index + 1}. (x: ${pin.xPct}%, y: ${pin.yPct}%)`
    )
    .join("\n");
  return [
    "Zonas marcadas en la imagen (origen arriba-izquierda; porcentajes del ancho y alto):",
    zones,
    instruction
      ? `Instrucción del usuario: ${instruction}`
      : "Instrucción del usuario: mejora la zona marcada.",
    "Aplica el cambio priorizando las zonas numeradas y conserva el resto de la composición.",
  ].join("\n");
}

type Props = {
  isOpen: boolean;
  imageSrc: string | null;
  onClose: () => void;
  onApply: (blob: Blob) => void | Promise<void>;
  busy?: boolean;
  /** Optional AI edit prompt panel (e.g. Social Automated Posts). */
  enableAiEdit?: boolean;
  aiEditBusy?: boolean;
  onAiEdit?: (prompt: string) => void | Promise<void>;
  aiCreditHint?: string;
  companyId?: string | null;
};

export function AiImageViewerEditorModal({
  isOpen,
  imageSrc,
  onClose,
  onApply,
  busy = false,
  enableAiEdit = false,
  aiEditBusy = false,
  onAiEdit,
  aiCreditHint,
  companyId = null,
}: Props) {
  const intl = useIntl();
  const [displaySrc, setDisplaySrc] = useState<string | null>(null);
  const [filterPresetId, setFilterPresetId] = useState("none");
  const [brightness, setBrightness] = useState(100);
  const [cropOpen, setCropOpen] = useState(false);
  const [cropBusy, setCropBusy] = useState(false);
  const [rotateBusyDir, setRotateBusyDir] = useState<null | "cw" | "ccw">(null);
  const [exportBusy, setExportBusy] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [aiPrompt, setAiPrompt] = useState("");
  const [pinMode, setPinMode] = useState(false);
  const [pins, setPins] = useState<ImagePin[]>([]);
  const [magicMode, setMagicMode] = useState(false);
  const [magicLayers, setMagicLayers] = useState<MagicLayerState[]>([]);
  const [selectedMagicId, setSelectedMagicId] = useState<string | null>(null);
  const [magicBusy, setMagicBusy] = useState(false);
  const ownedUrlsRef = useRef<Set<string>>(new Set());
  const initialSrcRef = useRef<string | null>(null);
  const previewImgRef = useRef<HTMLImageElement | null>(null);
  const magicDragRef = useRef<{
    pointerId: number;
    layerId: string;
    startClientX: number;
    startClientY: number;
    origXPct: number;
    origYPct: number;
  } | null>(null);

  const revokeOwned = useCallback((url: string | null) => {
    if (url && ownedUrlsRef.current.has(url)) {
      URL.revokeObjectURL(url);
      ownedUrlsRef.current.delete(url);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      ownedUrlsRef.current.forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch {
          /* empty */
        }
      });
      ownedUrlsRef.current.clear();
      setDisplaySrc(null);
      setCropOpen(false);
      setFilterPresetId("none");
      setBrightness(100);
      setApplyError(null);
      setAiPrompt("");
      setPinMode(false);
      setPins([]);
      setMagicMode(false);
      setMagicLayers([]);
      setSelectedMagicId(null);
      setMagicBusy(false);
      initialSrcRef.current = null;
      return;
    }
    if (!imageSrc) return;
    initialSrcRef.current = imageSrc;
    setDisplaySrc(imageSrc);
    setFilterPresetId("none");
    setBrightness(100);
    setApplyError(null);
    setCropOpen(false);
    setAiPrompt("");
    setPins([]);
    setPinMode(enableAiEdit);
    setMagicMode(false);
    setMagicLayers([]);
    setSelectedMagicId(null);
  }, [isOpen, imageSrc, enableAiEdit]);

  const presetCss =
    FILTER_PRESETS.find((p) => p.id === filterPresetId)?.css ?? "";
  const previewFilter = buildFilterCss(presetCss, brightness);

  const handleCropApply = async (blob: Blob) => {
    const url = URL.createObjectURL(blob);
    ownedUrlsRef.current.add(url);
    setDisplaySrc((prev) => {
      revokeOwned(prev);
      return url;
    });
    setCropOpen(false);
  };

  const handleRotate = async (direction: "cw" | "ccw") => {
    if (!displaySrc || rotateBusyDir) return;
    setRotateBusyDir(direction);
    setApplyError(null);
    try {
      const url = await rotateImageSrcToBlobUrl(displaySrc, direction);
      ownedUrlsRef.current.add(url);
      setDisplaySrc((prev) => {
        revokeOwned(prev);
        return url;
      });
    } catch {
      setApplyError(intl.formatMessage({ id: "app.aiGeneration.viewerRotateError" }));
    } finally {
      setRotateBusyDir(null);
    }
  };

  const handleResetEdits = () => {
    if (!initialSrcRef.current) return;
    setFilterPresetId("none");
    setBrightness(100);
    setPins([]);
    setMagicLayers([]);
    setSelectedMagicId(null);
    setDisplaySrc((prev) => {
      revokeOwned(prev);
      return initialSrcRef.current;
    });
  };

  const clearMagicLayer = useCallback(() => {
    setMagicLayers([]);
    setSelectedMagicId(null);
    magicDragRef.current = null;
  }, []);

  const runMagicDetection = useCallback(async () => {
    if (!displaySrc || !companyId) {
      setApplyError(
        intl.formatMessage({
          id: "app.aiGeneration.viewerMagicNeedCompany",
          defaultMessage: "No hay empresa disponible para detectar capas.",
        })
      );
      return;
    }
    setMagicBusy(true);
    setApplyError(null);
    try {
      const imageResponse = await fetch(displaySrc);
      const imageBlob = await imageResponse.blob();
      const file = new File([imageBlob], `layers-${Date.now()}.png`, {
        type: imageBlob.type || "image/png",
      });
      const formData = new FormData();
      formData.append("images", file);
      const { data } = await socialApiClient().post(
        "openai/image-layers",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          params: { companyId },
        }
      );
      const raw = Array.isArray(data?.payload?.layers)
        ? data.payload.layers
        : [];
      const next: MagicLayerState[] = [];
      for (const layer of raw) {
        const source: MagicLayerRectPct = {
          xPct: clampPct(layer.xPct),
          yPct: clampPct(layer.yPct),
          wPct: clampPct(layer.wPct),
          hPct: clampPct(layer.hPct),
        };
        if (source.wPct < 1 || source.hPct < 1) continue;
        let patchUrl: string | null = null;
        try {
          patchUrl = await extractMagicLayerPatch(displaySrc, source);
        } catch {
          patchUrl = null;
        }
        const text =
          layer.type === "text" && typeof layer.text === "string"
            ? layer.text
            : null;
        next.push({
          id: String(layer.id || `layer-${next.length + 1}`),
          type: layer.type || "graphic",
          label: String(layer.label || text || layer.type || "elemento"),
          text,
          draftText: text || "",
          source,
          current: { ...source },
          patchUrl,
        });
      }
      if (!next.length) {
        setApplyError(
          intl.formatMessage({
            id: "app.aiGeneration.viewerMagicEmpty",
            defaultMessage:
              "No se detectaron elementos. Probá con otra imagen o editá con IA.",
          })
        );
      }
      setMagicLayers(next);
      setSelectedMagicId(next[0]?.id ?? null);
    } catch {
      setApplyError(
        intl.formatMessage({
          id: "app.aiGeneration.viewerMagicError",
          defaultMessage: "No se pudo aplicar la capa mágica.",
        })
      );
      setMagicLayers([]);
      setSelectedMagicId(null);
    } finally {
      setMagicBusy(false);
    }
  }, [companyId, displaySrc, intl]);

  const enableMagicMode = () => {
    setMagicMode(true);
    setPinMode(false);
    setPins([]);
    clearMagicLayer();
    void runMagicDetection();
  };

  const enablePinMode = () => {
    setPinMode(true);
    setMagicMode(false);
    clearMagicLayer();
  };

  const selectedMagic = magicLayers.find((l) => l.id === selectedMagicId) || null;

  const handleLayerPointerDown = (
    e: React.PointerEvent<HTMLDivElement>,
    layerId: string
  ) => {
    if (!magicMode || locked || magicBusy) return;
    e.stopPropagation();
    e.preventDefault();
    const layer = magicLayers.find((l) => l.id === layerId);
    if (!layer) return;
    setSelectedMagicId(layerId);
    e.currentTarget.setPointerCapture(e.pointerId);
    magicDragRef.current = {
      pointerId: e.pointerId,
      layerId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      origXPct: layer.current.xPct,
      origYPct: layer.current.yPct,
    };
  };

  const handleLayerPointerMove = (
    e: React.PointerEvent<HTMLDivElement>
  ) => {
    const drag = magicDragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const img = previewImgRef.current;
    if (!img) return;
    const rect = img.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const dxPct = ((e.clientX - drag.startClientX) / rect.width) * 100;
    const dyPct = ((e.clientY - drag.startClientY) / rect.height) * 100;
    setMagicLayers((prev) =>
      prev.map((layer) => {
        if (layer.id !== drag.layerId) return layer;
        const maxX = Math.max(0, 100 - layer.current.wPct);
        const maxY = Math.max(0, 100 - layer.current.hPct);
        return {
          ...layer,
          current: {
            ...layer.current,
            xPct: clampPct(
              Math.max(0, Math.min(maxX, drag.origXPct + dxPct))
            ),
            yPct: clampPct(
              Math.max(0, Math.min(maxY, drag.origYPct + dyPct))
            ),
          },
        };
      })
    );
  };

  const handleLayerPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (magicDragRef.current?.pointerId === e.pointerId) {
      magicDragRef.current = null;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* empty */
      }
    }
  };

  const handleApplyMagicLayer = async () => {
    if (!displaySrc || !selectedMagic || magicBusy) return;
    const moved = !rectsEqual(selectedMagic.source, selectedMagic.current);
    const textChanged =
      selectedMagic.type === "text" &&
      (selectedMagic.draftText || "").trim() !==
        (selectedMagic.text || "").trim();

    if (!moved && !textChanged) return;

    setMagicBusy(true);
    setApplyError(null);
    try {
      if (textChanged && onAiEdit) {
        const prompt = [
          `En la pieza, edita SOLO el elemento de texto "${selectedMagic.label}".`,
          `Texto actual: "${selectedMagic.text || ""}".`,
          `Reemplazalo por exactamente: "${selectedMagic.draftText.trim()}".`,
          moved
            ? `Además mové ese bloque hacia aproximadamente x:${selectedMagic.current.xPct}% y:${selectedMagic.current.yPct}% (origen arriba-izquierda).`
            : "",
          "Conservá tipografía, color, tamaño relativo y el resto de la composición intactos.",
          "No agregues elementos nuevos ni cambies el fondo.",
        ]
          .filter(Boolean)
          .join(" ");
        await onAiEdit(prompt);
        clearMagicLayer();
        setMagicMode(false);
        return;
      }

      if (moved) {
        const blob = await applyMagicLayerMove(
          displaySrc,
          selectedMagic.source,
          selectedMagic.current
        );
        const url = URL.createObjectURL(blob);
        ownedUrlsRef.current.add(url);
        setDisplaySrc((prev) => {
          revokeOwned(prev);
          return url;
        });
        clearMagicLayer();
        // Re-detect on the new image so capas siguen disponibles.
        setTimeout(() => {
          void runMagicDetection();
        }, 50);
      }
    } catch {
      setApplyError(
        intl.formatMessage({
          id: "app.aiGeneration.viewerMagicError",
          defaultMessage: "No se pudo aplicar la capa mágica.",
        })
      );
    } finally {
      setMagicBusy(false);
    }
  };

  const handleDownload = async () => {
    if (!displaySrc) return;
    setExportBusy(true);
    setApplyError(null);
    try {
      const blob = await applyCanvasFilterToBlob(displaySrc, previewFilter);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `wembii-${Date.now()}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setApplyError(intl.formatMessage({ id: "app.aiGeneration.viewerExportError" }));
    } finally {
      setExportBusy(false);
    }
  };

  const handleApplyToResult = async () => {
    if (!displaySrc) return;
    setExportBusy(true);
    setApplyError(null);
    try {
      const blob = await applyCanvasFilterToBlob(displaySrc, previewFilter);
      await onApply(blob);
    } catch {
      setApplyError(intl.formatMessage({ id: "app.aiGeneration.viewerExportError" }));
    } finally {
      setExportBusy(false);
    }
  };

  const handleClose = () => {
    if (busy || exportBusy || cropBusy || rotateBusyDir) return;
    setCropOpen(false);
    onClose();
  };

  const locked = busy || exportBusy || !!rotateBusyDir || aiEditBusy || magicBusy;

  const handlePreviewClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!enableAiEdit || !pinMode || magicMode || locked) return;
    const img = previewImgRef.current;
    if (!img) return;
    const rect = img.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const xPct = clampPct(((e.clientX - rect.left) / rect.width) * 100);
    const yPct = clampPct(((e.clientY - rect.top) / rect.height) * 100);
    setPins((prev) => [
      ...prev,
      {
        key: `pin-${Date.now()}-${prev.length}`,
        xPct,
        yPct,
      },
    ]);
  };

  const removePin = (key: string) => {
    setPins((prev) => prev.filter((pin) => pin.key !== key));
  };

  const handleAiEditSubmit = async () => {
    if (!onAiEdit || locked) return;
    if (!aiPrompt.trim() && pins.length === 0) return;
    setApplyError(null);
    try {
      await onAiEdit(buildPinnedAiPrompt(aiPrompt, pins));
      setAiPrompt("");
      setPins([]);
    } catch {
      setApplyError(
        intl.formatMessage({
          id: "app.aiGeneration.viewerAiEditError",
          defaultMessage: "No se pudo editar la imagen con IA.",
        })
      );
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen && !!displaySrc && !cropOpen}
        toggle={handleClose}
        className="ai-image-viewer-modal"
        contentClassName="ai-image-viewer-modal__dialog-content"
        modalClassName="ai-image-viewer-modal__backdrop-layer"
        backdropClassName="ai-image-viewer-modal__backdrop"
        centered
        backdrop="static"
        fade={false}
        zIndex={2050}
      >
        <div className="ai-image-viewer-modal__chrome">
          <div className="ai-image-viewer-modal__header">
            <div className="ai-image-viewer-modal__header-brand">
              <span className="ai-image-viewer-modal__badge" aria-hidden>
                <FontAwesomeIcon icon={faWandMagicSparkles} />
                AI
              </span>
              <div>
                <h2 className="ai-image-viewer-modal__title">
                  {intl.formatMessage({ id: "app.aiGeneration.viewerModalTitle" })}
                </h2>
                <p className="ai-image-viewer-modal__subtitle">
                  {intl.formatMessage({ id: "app.aiGeneration.viewerModalSubtitle" })}
                </p>
              </div>
            </div>
            <button
              type="button"
              className="ai-image-viewer-modal__close"
              onClick={handleClose}
              disabled={locked}
              aria-label={intl.formatMessage({ id: "app.aiGeneration.viewerCloseBtn" })}
            >
              ×
            </button>
          </div>

          <ModalBody className="ai-image-viewer-modal__body">
            {displaySrc && (
              <>
                <div className="ai-image-viewer-modal__preview-shell">
                  <div className="ai-image-viewer-modal__preview-grid" aria-hidden />
                  <div className="ai-image-viewer-modal__preview-glow" aria-hidden />
                  <div
                    className={
                      "ai-image-viewer-modal__preview" +
                      (enableAiEdit && pinMode && !magicMode
                        ? " ai-image-viewer-modal__preview--pinning"
                        : "") +
                      (magicMode
                        ? " ai-image-viewer-modal__preview--magic"
                        : "")
                    }
                  >
                    <div
                      className="ai-image-viewer-modal__preview-stage"
                      onClick={handlePreviewClick}
                      role={
                        enableAiEdit && pinMode && !magicMode
                          ? "button"
                          : undefined
                      }
                      tabIndex={
                        enableAiEdit && pinMode && !magicMode ? 0 : undefined
                      }
                      aria-label={
                        magicMode
                          ? intl.formatMessage({
                              id: "app.aiGeneration.viewerMagicSelectHint",
                              defaultMessage:
                                "Seleccioná un elemento detectado y movelo",
                            })
                          : enableAiEdit && pinMode
                            ? intl.formatMessage({
                                id: "app.aiGeneration.viewerPinClickHint",
                                defaultMessage:
                                  "Haz clic en la imagen para marcar una zona",
                              })
                            : undefined
                      }
                    >
                      <img
                        ref={previewImgRef}
                        src={displaySrc}
                        alt=""
                        style={{ filter: previewFilter || "none" }}
                        draggable={false}
                      />
                      {magicMode
                        ? magicLayers.map((layer) => {
                            const selected = layer.id === selectedMagicId;
                            const moved = !rectsEqual(
                              layer.source,
                              layer.current
                            );
                            return (
                              <React.Fragment key={layer.id}>
                                {selected && moved ? (
                                  <div
                                    className="ai-image-viewer-modal__magic-hole"
                                    style={{
                                      left: `${layer.source.xPct}%`,
                                      top: `${layer.source.yPct}%`,
                                      width: `${layer.source.wPct}%`,
                                      height: `${layer.source.hPct}%`,
                                    }}
                                    aria-hidden
                                  />
                                ) : null}
                                <div
                                  className={
                                    "ai-image-viewer-modal__magic-box" +
                                    (selected
                                      ? " ai-image-viewer-modal__magic-box--selected"
                                      : "") +
                                    (layer.type === "text"
                                      ? " ai-image-viewer-modal__magic-box--text"
                                      : "")
                                  }
                                  style={{
                                    left: `${layer.current.xPct}%`,
                                    top: `${layer.current.yPct}%`,
                                    width: `${layer.current.wPct}%`,
                                    height: `${layer.current.hPct}%`,
                                  }}
                                  onPointerDown={(e) =>
                                    handleLayerPointerDown(e, layer.id)
                                  }
                                  onPointerMove={handleLayerPointerMove}
                                  onPointerUp={handleLayerPointerUp}
                                  onPointerCancel={handleLayerPointerUp}
                                  title={layer.label}
                                >
                                  {selected && layer.patchUrl ? (
                                    <img
                                      className="ai-image-viewer-modal__magic-box-patch"
                                      src={layer.patchUrl}
                                      alt=""
                                      draggable={false}
                                    />
                                  ) : null}
                                  <span className="ai-image-viewer-modal__magic-box-label">
                                    {layer.type === "text" ? "T" : "•"}{" "}
                                    {layer.label.slice(0, 28)}
                                  </span>
                                  {selected ? (
                                    <span className="ai-image-viewer-modal__magic-layer-hint">
                                      <FontAwesomeIcon icon={faHand} />
                                    </span>
                                  ) : null}
                                </div>
                              </React.Fragment>
                            );
                          })
                        : null}
                      {enableAiEdit && !magicMode
                        ? pins.map((pin, index) => (
                            <button
                              key={pin.key}
                              type="button"
                              className="ai-image-viewer-modal__pin"
                              style={{
                                left: `${pin.xPct}%`,
                                top: `${pin.yPct}%`,
                              }}
                              title={intl.formatMessage(
                                {
                                  id: "app.aiGeneration.viewerPinRemoveTitle",
                                  defaultMessage: "Quitar marca {n}",
                                },
                                { n: index + 1 }
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                removePin(pin.key);
                              }}
                              disabled={locked}
                            >
                              {index + 1}
                            </button>
                          ))
                        : null}
                    </div>
                  </div>
                </div>

                <section className="ai-image-viewer-modal__section" aria-labelledby="ai-viewer-filters-label">
                  <div className="ai-image-viewer-modal__section-head">
                    <span id="ai-viewer-filters-label" className="ai-image-viewer-modal__section-title">
                      {intl.formatMessage({ id: "app.aiGeneration.viewerFiltersLabel" })}
                    </span>
                    <span className="ai-image-viewer-modal__section-hint">
                      {intl.formatMessage({ id: "app.aiGeneration.viewerFiltersHint" })}
                    </span>
                  </div>
                  <div className="ai-image-viewer-modal__filters-scroll">
                    {FILTER_PRESETS.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        className={
                          "ai-image-viewer-modal__filter-pill" +
                          (filterPresetId === p.id
                            ? " ai-image-viewer-modal__filter-pill--active"
                            : "")
                        }
                        onClick={() => setFilterPresetId(p.id)}
                        disabled={locked}
                      >
                        <FontAwesomeIcon icon={p.icon} className="ai-image-viewer-modal__filter-pill-icon" />
                        <span>{intl.formatMessage({ id: p.labelKey })}</span>
                      </button>
                    ))}
                  </div>
                </section>

                <section className="ai-image-viewer-modal__tuning" aria-labelledby="ai-viewer-brightness-label">
                  <div className="ai-image-viewer-modal__tuning-inner">
                    <div className="ai-image-viewer-modal__brightness-head">
                      <FontAwesomeIcon icon={faSun} className="ai-image-viewer-modal__brightness-icon" />
                      <span id="ai-viewer-brightness-label">
                        {intl.formatMessage({ id: "app.aiGeneration.viewerBrightnessLabel" })}
                      </span>
                      <span className="ai-image-viewer-modal__brightness-value">{brightness}%</span>
                    </div>
                    <input
                      type="range"
                      className="ai-image-viewer-modal__range"
                      min={60}
                      max={140}
                      value={brightness}
                      onChange={(e) => setBrightness(Number(e.target.value))}
                      disabled={locked}
                      aria-label={intl.formatMessage({
                        id: "app.aiGeneration.viewerBrightnessLabel",
                      })}
                    />
                  </div>

                  <div className="ai-image-viewer-modal__quick-tools">
                    <button
                      type="button"
                      className="ai-image-viewer-modal__tool-btn"
                      onClick={() => handleRotate("ccw")}
                      disabled={locked}
                      title={intl.formatMessage({
                        id: "app.aiGeneration.viewerRotateLeftTitle",
                      })}
                      aria-label={intl.formatMessage({
                        id: "app.aiGeneration.viewerRotateLeftTitle",
                      })}
                    >
                      {rotateBusyDir === "ccw" ? (
                        <Spinner size="sm" className="ai-image-viewer-modal__tool-spinner" />
                      ) : (
                        <FontAwesomeIcon icon={faRotateLeft} />
                      )}
                      <span>{intl.formatMessage({ id: "app.aiGeneration.viewerRotateLeftBtn" })}</span>
                    </button>
                    <button
                      type="button"
                      className="ai-image-viewer-modal__tool-btn"
                      onClick={() => handleRotate("cw")}
                      disabled={locked}
                      title={intl.formatMessage({
                        id: "app.aiGeneration.viewerRotateRightTitle",
                      })}
                      aria-label={intl.formatMessage({
                        id: "app.aiGeneration.viewerRotateRightTitle",
                      })}
                    >
                      {rotateBusyDir === "cw" ? (
                        <Spinner size="sm" className="ai-image-viewer-modal__tool-spinner" />
                      ) : (
                        <FontAwesomeIcon icon={faRotateRight} />
                      )}
                      <span>{intl.formatMessage({ id: "app.aiGeneration.viewerRotateRightBtn" })}</span>
                    </button>
                    <button
                      type="button"
                      className={
                        "ai-image-viewer-modal__tool-btn" +
                        (magicMode
                          ? " ai-image-viewer-modal__tool-btn--active"
                          : "")
                      }
                      onClick={() => {
                        if (magicMode) {
                          setMagicMode(false);
                          clearMagicLayer();
                        } else {
                          enableMagicMode();
                        }
                      }}
                      disabled={locked}
                      title={intl.formatMessage({
                        id: "app.aiGeneration.viewerMagicTitle",
                        defaultMessage:
                          "Capa mágica: seleccioná un elemento y movelo con el mouse",
                      })}
                    >
                      {magicBusy ? (
                        <Spinner size="sm" className="ai-image-viewer-modal__tool-spinner" />
                      ) : (
                        <FontAwesomeIcon icon={faLayerGroup} />
                      )}
                      <span>
                        {intl.formatMessage({
                          id: "app.aiGeneration.viewerMagicBtn",
                          defaultMessage: "Capa mágica",
                        })}
                      </span>
                    </button>
                    <button
                      type="button"
                      className="ai-image-viewer-modal__tool-btn"
                      onClick={() => setCropOpen(true)}
                      disabled={locked}
                    >
                      <FontAwesomeIcon icon={faCrop} />
                      <span>{intl.formatMessage({ id: "app.aiGeneration.cropBtn" })}</span>
                    </button>
                    <button
                      type="button"
                      className="ai-image-viewer-modal__tool-btn"
                      onClick={handleResetEdits}
                      disabled={locked}
                    >
                      <FontAwesomeIcon icon={faUndo} />
                      <span>{intl.formatMessage({ id: "app.aiGeneration.viewerResetBtn" })}</span>
                    </button>
                    <button
                      type="button"
                      className="ai-image-viewer-modal__tool-btn"
                      onClick={handleDownload}
                      disabled={locked || exportBusy}
                    >
                      {exportBusy ? (
                        <Spinner size="sm" className="ai-image-viewer-modal__tool-spinner" />
                      ) : (
                        <FontAwesomeIcon icon={faDownload} />
                      )}
                      <span>{intl.formatMessage({ id: "app.aiGeneration.viewerDownloadBtn" })}</span>
                    </button>
                  </div>

                  {magicMode ? (
                    <div className="ai-image-viewer-modal__magic-bar" role="status">
                      <div className="ai-image-viewer-modal__magic-bar-text">
                        <FontAwesomeIcon icon={faLayerGroup} />
                        <span>
                          {magicBusy
                            ? intl.formatMessage({
                                id: "app.aiGeneration.viewerMagicDetecting",
                                defaultMessage:
                                  "Detectando elementos gráficos…",
                              })
                            : selectedMagic
                              ? intl.formatMessage(
                                  {
                                    id: "app.aiGeneration.viewerMagicSelected",
                                    defaultMessage:
                                      "Elemento: {label}. Arrastrá para mover{textHint}.",
                                  },
                                  {
                                    label: selectedMagic.label,
                                    textHint:
                                      selectedMagic.type === "text"
                                        ? " o editá el texto abajo"
                                        : "",
                                  }
                                )
                              : intl.formatMessage({
                                  id: "app.aiGeneration.viewerMagicSelectHint",
                                  defaultMessage:
                                    "Seleccioná un elemento detectado y movelo",
                                })}
                        </span>
                      </div>
                      {selectedMagic?.type === "text" ? (
                        <div className="ai-image-viewer-modal__magic-text-edit">
                          <label htmlFor="ai-magic-text-input">
                            {intl.formatMessage({
                              id: "app.aiGeneration.viewerMagicTextLabel",
                              defaultMessage: "Texto del elemento",
                            })}
                          </label>
                          <textarea
                            id="ai-magic-text-input"
                            rows={3}
                            value={selectedMagic.draftText}
                            disabled={locked}
                            onChange={(e) => {
                              const value = e.target.value;
                              setMagicLayers((prev) =>
                                prev.map((layer) =>
                                  layer.id === selectedMagic.id
                                    ? { ...layer, draftText: value }
                                    : layer
                                )
                              );
                            }}
                          />
                        </div>
                      ) : null}
                      <div className="ai-image-viewer-modal__magic-bar-actions">
                        <button
                          type="button"
                          className="ai-image-viewer-modal__tool-btn"
                          onClick={() => void runMagicDetection()}
                          disabled={locked}
                        >
                          <span>
                            {intl.formatMessage({
                              id: "app.aiGeneration.viewerMagicRedetect",
                              defaultMessage: "Volver a detectar",
                            })}
                          </span>
                        </button>
                        {selectedMagic ? (
                          <button
                            type="button"
                            className="ai-image-viewer-modal__pin-cta ai-image-viewer-modal__pin-cta--active"
                            onClick={handleApplyMagicLayer}
                            disabled={
                              locked ||
                              (rectsEqual(
                                selectedMagic.source,
                                selectedMagic.current
                              ) &&
                                (selectedMagic.draftText || "").trim() ===
                                  (selectedMagic.text || "").trim())
                            }
                          >
                            {magicBusy ? (
                              <Spinner size="sm" />
                            ) : (
                              <FontAwesomeIcon icon={faHand} />
                            )}
                            <span>
                              {intl.formatMessage({
                                id: "app.aiGeneration.viewerMagicApply",
                                defaultMessage: "Aplicar cambios",
                              })}
                            </span>
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="ai-image-viewer-modal__tool-btn"
                          onClick={() => {
                            clearMagicLayer();
                            setMagicMode(false);
                          }}
                          disabled={locked}
                        >
                          <span>
                            {intl.formatMessage({
                              id: "app.aiGeneration.viewerMagicCancel",
                              defaultMessage: "Salir de capa mágica",
                            })}
                          </span>
                        </button>
                      </div>
                    </div>
                  ) : null}
                </section>

                {enableAiEdit && onAiEdit ? (
                  <section
                    className="ai-image-viewer-modal__section ai-image-viewer-modal__ai-edit"
                    aria-labelledby="ai-viewer-ai-edit-label"
                  >
                    <div className="ai-image-viewer-modal__section-head">
                      <span
                        id="ai-viewer-ai-edit-label"
                        className="ai-image-viewer-modal__section-title"
                      >
                        {intl.formatMessage({
                          id: "app.aiGeneration.viewerAiEditTitle",
                          defaultMessage: "Editar con IA",
                        })}
                      </span>
                    </div>
                    <div className="ai-image-viewer-modal__pin-callout" role="note">
                      <span className="ai-image-viewer-modal__pin-callout-icon" aria-hidden>
                        <FontAwesomeIcon icon={faLocationDot} />
                      </span>
                      <div className="ai-image-viewer-modal__pin-callout-body">
                        <strong className="ai-image-viewer-modal__pin-callout-title">
                          {intl.formatMessage({
                            id: "app.aiGeneration.viewerPinCalloutTitle",
                            defaultMessage: "Marca zonas en la imagen",
                          })}
                        </strong>
                        <p className="ai-image-viewer-modal__pin-callout-text">
                          {intl.formatMessage({
                            id: "app.aiGeneration.viewerAiEditHintPinned",
                            defaultMessage:
                              "Haz clic para colocar numeritos y describe el cambio. La IA recibe las coordenadas exactas.",
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="ai-image-viewer-modal__pin-toolbar">
                      <button
                        type="button"
                        className={
                          "ai-image-viewer-modal__pin-cta" +
                          (pinMode && !magicMode
                            ? " ai-image-viewer-modal__pin-cta--active"
                            : "")
                        }
                        onClick={() => {
                          if (pinMode && !magicMode) {
                            setPinMode(false);
                          } else {
                            enablePinMode();
                          }
                        }}
                        disabled={locked}
                      >
                        <FontAwesomeIcon icon={faLocationDot} />
                        <span>
                          {pinMode && !magicMode
                            ? intl.formatMessage({
                                id: "app.aiGeneration.viewerPinModeOn",
                                defaultMessage: "Marcando zonas…",
                              })
                            : intl.formatMessage({
                                id: "app.aiGeneration.viewerPinModeOff",
                                defaultMessage: "Marcar zona",
                              })}
                        </span>
                      </button>
                      {pins.length > 0 ? (
                        <button
                          type="button"
                          className="ai-image-viewer-modal__tool-btn"
                          onClick={() => setPins([])}
                          disabled={locked}
                        >
                          <span>
                            {intl.formatMessage(
                              {
                                id: "app.aiGeneration.viewerPinClear",
                                defaultMessage: "Limpiar marcas ({n})",
                              },
                              { n: pins.length }
                            )}
                          </span>
                        </button>
                      ) : null}
                    </div>
                    {pins.length > 0 ? (
                      <ul className="ai-image-viewer-modal__pin-list">
                        {pins.map((pin, index) => (
                          <li key={pin.key}>
                            <strong>{index + 1}.</strong>{" "}
                            (x: {pin.xPct}%, y: {pin.yPct}%)
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    <textarea
                      className="ai-image-viewer-modal__ai-textarea"
                      value={aiPrompt}
                      onChange={(e) => setAiPrompt(e.target.value)}
                      disabled={locked}
                      rows={3}
                      placeholder={intl.formatMessage({
                        id: "app.aiGeneration.viewerAiEditPlaceholderPinned",
                        defaultMessage:
                          "Ej: cambia ese icono, agranda el texto, mejora el contraste…",
                      })}
                    />
                    <div className="ai-image-viewer-modal__ai-actions">
                      <button
                        type="button"
                        className="ai-image-viewer-modal__btn ai-image-viewer-modal__btn--primary"
                        onClick={handleAiEditSubmit}
                        disabled={
                          locked || (!aiPrompt.trim() && pins.length === 0)
                        }
                      >
                        {aiEditBusy ? (
                          <>
                            <Spinner size="sm" className="mr-2" />
                            {intl.formatMessage({
                              id: "app.aiGeneration.viewerAiEditApplying",
                              defaultMessage: "Aplicando con IA…",
                            })}
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faWandMagicSparkles} className="mr-2" />
                            {intl.formatMessage({
                              id: "app.aiGeneration.viewerAiEditBtn",
                              defaultMessage: "Aplicar con IA",
                            })}
                          </>
                        )}
                      </button>
                      {aiCreditHint ? (
                        <span className="ai-image-viewer-modal__ai-credit">{aiCreditHint}</span>
                      ) : null}
                    </div>
                  </section>
                ) : null}

                <p className="ai-image-viewer-modal__foot-note">
                  {intl.formatMessage({ id: "app.aiGeneration.viewerHint" })}
                </p>

                {applyError && (
                  <p className="ai-image-viewer-modal__error">{applyError}</p>
                )}
              </>
            )}
          </ModalBody>

          <ModalFooter className="ai-image-viewer-modal__footer">
            <button
              type="button"
              className="ai-image-viewer-modal__btn ai-image-viewer-modal__btn--ghost"
              onClick={handleClose}
              disabled={locked}
            >
              {intl.formatMessage({ id: "app.aiGeneration.viewerCloseBtn" })}
            </button>
            <button
              type="button"
              className="ai-image-viewer-modal__btn ai-image-viewer-modal__btn--primary"
              onClick={handleApplyToResult}
              disabled={locked || !displaySrc}
            >
              {exportBusy || busy ? (
                <>
                  <Spinner size="sm" className="ai-image-viewer-modal__btn-spinner" />
                  {intl.formatMessage({ id: "app.aiGeneration.viewerApplying" })}
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faWandMagicSparkles} />
                  {intl.formatMessage({ id: "app.aiGeneration.viewerApplyBtn" })}
                </>
              )}
            </button>
          </ModalFooter>
        </div>
      </Modal>

      <AiImageCropModal
        isOpen={cropOpen && !!displaySrc}
        imageSrc={displaySrc}
        onClose={() => !cropBusy && setCropOpen(false)}
        onApply={async (blob) => {
          setCropBusy(true);
          try {
            await handleCropApply(blob);
          } finally {
            setCropBusy(false);
          }
        }}
        busy={cropBusy}
      />
    </>
  );
}
